import { Component, computed, inject, Input, Signal, ViewChild } from '@angular/core';
import { MenuItem, ConfirmationService } from 'primeng/api';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuModule } from 'primeng/menu';
import { AppConfigurator } from './app.configurator';
import { LayoutService } from '../service/layout.service';
import { TranslationService, LANGUAGES, Language } from '../service/translation.service';
import { Avatar } from 'primeng/avatar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ApiService } from '../../../api/api.service';
import { MessageService } from 'primeng/api';
import md5 from 'md5';
import { VersionInfoDialogComponent } from '@/cloud/components/version-info-dialog/version-info-dialog.component';
import { PortalFeedbackComponent } from '@/cloud/components/portal-feedback/portal-feedback.component';
import { AssistantToggleComponent } from '@/cloud/components/assistant/assistant-toggle.component';

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [RouterModule, CommonModule, StyleClassModule, AppConfigurator, MenuModule, Avatar, ConfirmDialogModule, VersionInfoDialogComponent, PortalFeedbackComponent, AssistantToggleComponent],
    providers: [ConfirmationService, MessageService],
    template: ` <div class="layout-topbar" [class.layout-topbar-device]="!isCloudInstanceInput" [class.layout-topbar-cloud]="isCloudInstanceInput">
        <p-confirmdialog />
        <div class="layout-topbar-logo-container">
            <button class="layout-menu-button layout-topbar-action" (click)="layoutService.onMenuToggle()">
                <i class="pi pi-bars"></i>
            </button>
            <a class="layout-topbar-logo" routerLink="/">
                <svg width="165" style="margin-top: 3px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 165 38.343">
                    <g transform="translate(-192.662 -398.538)">
                        <g transform="translate(283.839 398.538)">
                            <path
                                d="M305.921,428.649l13.742-23.8c.072-.121.139-.217.195-.294a2.422,2.422,0,0,1,.2.3l13.743,23.8h6.72L325.1,401.935a5.736,5.736,0,0,0-10.475,0L299.2,428.649Z"
                                transform="translate(-299.199 -398.538)"
                                style="fill: var(--p-primary-color, #7ab800)"
                            />
                        </g>
                        <g transform="translate(316.34 399.47)">
                            <path
                                d="M371.776,399.628l-13.744,23.8a2.8,2.8,0,0,1-.195.3,2.782,2.782,0,0,1-.2-.3l-13.743-23.8h-6.72L352.6,426.342a5.736,5.736,0,0,0,10.475,0L378.5,399.628Z"
                                transform="translate(-337.176 -399.628)"
                                style="fill: var(--p-primary-color, #7ab800)"
                            />
                        </g>
                        <g transform="translate(192.662 398.671)">
                            <path
                                d="M198.483,437.037h-5.821V409.521a9.911,9.911,0,0,1,9.9-9.9h8.053a14.608,14.608,0,0,1,0,29.216h-8.538v-5.826h8.538a8.782,8.782,0,0,0,0-17.564h-8.053a4.081,4.081,0,0,0-4.075,4.078v27.516Z"
                                transform="translate(-192.662 -398.828)"
                                fill="#868686"
                            />
                            <g transform="translate(35.513 0.789)">
                                <path
                                    d="M259.588,402.722a14.5,14.5,0,0,0-9-3.1h-6.5a9.909,9.909,0,0,0-9.9,9.9V428.8h5.821V409.515a4.081,4.081,0,0,1,4.076-4.075h6.5a8.714,8.714,0,0,1,5.979,2.363A18.432,18.432,0,0,1,259.588,402.722Z"
                                    transform="translate(-234.193 -399.617)"
                                    fill="#868686"
                                />
                            </g>
                            <path
                                d="M279.622,429.48c-8.972,0-16.268-6.906-16.268-15.392s7.3-15.393,16.268-15.393,16.27,6.9,16.27,15.393-7.3,15.392-16.27,15.392Zm0-24.965c-5.76,0-10.446,4.294-10.446,9.573s4.686,9.573,10.446,9.573,10.448-4.3,10.448-9.573-4.688-9.573-10.448-9.573Z"
                                transform="translate(-202.906 -398.694)"
                                fill="#868686"
                            />
                        </g>
                    </g>
                </svg>
                @if (isCloudInstanceInput) {
                    <i class="fa fa-cloud"></i>
                }
            </a>
        </div>

        @if (isCloudInstanceInput) {
            <div class="layout-topbar-tenant">
                <ng-content select="[tenant-switcher]" />
            </div>
        }
        <div class="layout-topbar-actions">
            @if (!isCloudInstanceInput) {
                <span class="system-name" [title]="systemNameInput?.() ?? ''">{{ systemNameInput?.() ?? '' }}</span>
            }
            <div class="layout-config-menu">
                <!-- <button type="button" class="layout-topbar-action" (click)="toggleDarkMode()">
                    <i [ngClass]="{ 'pi ': true, 'pi-moon': layoutService.isDarkTheme(), 'pi-sun': !layoutService.isDarkTheme() }"></i>
                </button> -->
                @if (isCloudInstanceInput) {
                    <app-assistant-toggle />
                    <app-portal-feedback />
                    <button type="button" class="layout-topbar-action" (click)="openVersionInfo()" [title]="translationService.t()('topbar.versionInfo')">
                        <i class="pi pi-info-circle"></i>
                    </button>
                }
                <!-- Language switcher: device mode only until the cloud portal is translated -->
                @if (!isCloudInstanceInput) {
                <div class="relative">
                    <button
                        class="layout-topbar-action layout-topbar-action-lang"
                        pStyleClass="@next"
                        enterFromClass="hidden"
                        enterActiveClass="animate-scalein"
                        leaveToClass="hidden"
                        leaveActiveClass="animate-fadeout"
                        [hideOnOutsideClick]="true"
                        [title]="translationService.t()('topbar.language')"
                    >
                        <span class="lang-flag">{{ currentFlag() }}</span>
                    </button>
                    <div class="hidden absolute top-full right-0 z-50 bg-surface-0 dark:bg-surface-900 rounded-border shadow-md p-2 origin-top min-w-32">
                        @for (lang of languages; track lang.code) {
                            <button
                                class="lang-option flex items-center gap-2 w-full text-left px-3 py-2 rounded-border hover:bg-emphasis cursor-pointer border-none bg-transparent text-color"
                                [class.font-bold]="translationService.currentLanguage() === lang.code"
                                (click)="switchLanguage(lang.code)"
                            >
                                <span class="lang-flag">{{ lang.flag }}</span>
                                <span>{{ lang.label }}</span>
                            </button>
                        }
                    </div>
                </div>
                }
                <div class="relative">
                    <button class="layout-topbar-action" pStyleClass="@next" enterFromClass="hidden" enterActiveClass="animate-scalein" leaveToClass="hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true">
                        <i [class]="isCloudInstanceInput ? 'pi pi-palette' : 'far fa-palette'"></i>
                    </button>
                    <app-configurator />
                </div>
            </div>

            <div class="layout-topbar-menu hidden lg:block">
                <div class="layout-topbar-menu-content">
                    @if (!isCloudInstanceInput && loggedInUserInput?.() != null) {
                        <button type="button" class="layout-topbar-action" (click)="systemMenu.toggle($event)" [title]="translationService.t()('topbar.systemActions')">
                            <i class="far fa-cog"></i>
                        </button>
                        <p-menu #systemMenu [model]="systemMenuItems()" [popup]="true" />
                    }
                    @if (loggedInUserInput?.() != null) {
                        <button type="button" class="layout-topbar-action layout-topbar-action-highlight" (click)="userMenu.toggle($event)">
                            @if (gravatarUrl()) {
                                <p-avatar style="height: 32px; width: 32px;" [image]="gravatarUrl()!" shape="circle" class="avatar-image"></p-avatar>
                            } @else {
                                <i class="pi pi-user" style="font-size: 1.5em;"></i>
                            }
                        </button>
                        <p-menu #userMenu [model]="userMenuItems()" [popup]="true" />
                    } @else {
                        <button type="button" class="layout-topbar-action" routerLink="/auth/login">
                            <i class="pi pi-sign-in"></i>
                            <span>{{ translationService.t()('topbar.login') }}</span>
                        </button>
                    }
                </div>
            </div>
        </div>
        <app-version-info-dialog #versionDialog />
    </div>`
})
export class AppTopbar {
    private router = inject(Router);
    private confirmationService = inject(ConfirmationService);
    private messageService = inject(MessageService);
    private apiService = inject(ApiService);
    translationService = inject(TranslationService);
    // Inputs allow parent components to override the displayed system name and logged in user.
    // Use Angular v17 signal inputs if provided, otherwise fall back to ApiService signals.
    @Input() systemNameInput?: Signal<string | undefined>;
    @Input() loggedInUserInput?: Signal<string | undefined>;
    @Input() isCloudInstanceInput?: boolean | undefined;
    @Input() userEmailForGravatarInput?: Signal<string | undefined>;

    @ViewChild('versionDialog') versionDialog!: VersionInfoDialogComponent;

    languages = LANGUAGES;
    currentFlag = computed(() => {
        const lang = this.translationService.currentLanguage();
        return LANGUAGES.find((l) => l.code === lang)?.flag ?? '🇬🇧';
    });

    switchLanguage(lang: Language): void {
        this.translationService.setLanguage(lang);
    }

    systemMenuItems = computed<MenuItem[]>(() => {
        const t = this.translationService.t();
        return [
            {
                label: t('topbar.systemActions'),
                items: [
                    {
                        label: t('topbar.restartApp'),
                        icon: 'pi pi-refresh',
                        command: () => this.confirmRestartApp()
                    },
                    {
                        label: t('topbar.rebootAppliance'),
                        icon: 'pi pi-power-off',
                        command: () => this.confirmRebootAppliance(),
                        visible: this.apiService.statusData().devicePlatform === 'Appliance'
                    }
                ]
            }
        ];
    });

    userMenuItems = computed<MenuItem[]>(() => {
        const t = this.translationService.t();
        if (this.loggedInUserInput) {
            const username = String(this.loggedInUserInput())
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
            return [
                {
                    label: username,
                    icon: 'pi pi-user',
                    disabled: true
                },
                {
                    separator: true
                },
                {
                    label: t('topbar.myProfile'),
                    icon: 'pi pi-user-edit',
                    routerLink: '/profile'
                },
                {
                    label: t('topbar.logout'),
                    icon: 'pi pi-sign-out',
                    command: () => {
                        this.router.navigateByUrl('/auth/logout');
                    }
                }
            ];
        } else {
            return [
                {
                    label: t('topbar.notLoggedIn'),
                    disabled: true,
                    icon: 'pi pi-user'
                },
                {
                    label: t('topbar.login'),
                    icon: 'pi pi-sign-in',
                    routerLink: '/auth/login'
                }
            ];
        }
    });
    gravatarUrl = computed<string | null>(() => {
        const email = this.userEmailForGravatarInput ? this.userEmailForGravatarInput() : null;
        if (!email) return null;
        const hash = this.hashEmailForGravatar(email);
        return `https://www.gravatar.com/avatar/${hash}?s=40&d=identicon`;
    });

    constructor(public layoutService: LayoutService) {}

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
    }

    openVersionInfo() {
        this.versionDialog.open();
    }

    private hashEmailForGravatar(email: string): string {
        return md5(email.trim().toLowerCase());
    }

    confirmRestartApp() {
        const t = this.translationService.t();
        this.confirmationService.confirm({
            message: t('confirm.restartMessage'),
            header: t('confirm.restartHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: t('confirm.restart'),
            acceptButtonStyleClass: 'p-button-warning',
            rejectLabel: t('cloud.cancel'),
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => {
                this.apiService
                    .restartApp()
                    .then((response) => {
                        // Navigate to reconnecting page
                        this.router.navigate(['/reconnecting'], {
                            state: { action: 'restart' },
                            skipLocationChange: false
                        });
                    })
                    .catch((error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: error.message || 'Failed to restart application'
                        });
                    });
            }
        });
    }

    confirmRebootAppliance() {
        const t = this.translationService.t();
        this.confirmationService.confirm({
            message: t('confirm.rebootMessage'),
            header: t('confirm.rebootHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: t('confirm.reboot'),
            acceptButtonStyleClass: 'p-button-danger',
            rejectLabel: t('cloud.cancel'),
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => {
                this.apiService
                    .rebootAppliance()
                    .then((response) => {
                        console.log('Reboot response:', response);
                        // Navigate to reconnecting page
                        this.router.navigate(['/reconnecting'], {
                            state: { action: 'reboot' },
                            skipLocationChange: false
                        });
                    })
                    .catch((error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: error.message || 'Failed to reboot appliance'
                        });
                    });
            }
        });
    }
}
